import type { Duo } from '.';
import { DuoItem } from '../buruburu/care/care.ruleset';
import { PetSpecies } from '../buruburu/pet/pet.ruleset';
import { defineAsyncGetters } from '../utils';

export const duoAsyncGetters = (Duo: Duo) => {
  return defineAsyncGetters<Duo>()({
    getAllDuoInvitations: async (playerId: string, api) => {
      const { state } = (await api.fetchStates([playerId]))[playerId];
      const now = api.date.now();

      return [
        ...Duo.api.getNonExpiredSentInvitations(state, now),
        ...Duo.api.getNonExpiredReceivedInvitations(state, now),
      ];
    },
    getDuoInvitationsDetails: async (
      _: void,
      api,
    ): Promise<{ sent: DuoFriend[]; received: DuoFriend[] }> => {
      const state = await api.getOwnState();
      const { sent, received } = state.duo.invitations;
      const { visits, visited } = state.visit;

      const profiles = await api.searchPlayers({
        where: {
          id: {
            isOneOf: [
              ...sent.map(({ playerId }) => playerId),
              ...received.map(({ playerId }) => playerId),
            ],
          },
        },
        limit: sent.length + received.length,
      });

      const map = (original: typeof sent[number]): DuoFriend => {
        const profile = profiles.results.find(
          (x) => x.id === original.playerId,
        );
        return {
          id: original.playerId,
          profile: {
            name: profile?.publicProfileName ?? 'unknown',
            photo: profile?.publicProfilePhoto ?? '',
            lastLogin: (profile as any)?.updatedAt ?? 0,
            visits: Object.keys(visits).filter(
              (playerId) => playerId === original.playerId,
            ).length,
            visited: Object.keys(visited).filter(
              (playerId) => playerId === original.playerId,
            ).length,
          },
          pet: {
            name: original.pet.name ?? 'unknown',
            species: (original.pet.species ?? 'giraffe') as PetSpecies,
            age: original.pet.age ?? 0,
          },
          invitation: {
            timestamp: original.timestamp,
            item: original.careItemId as DuoItem,
            status: original.status,
            lastReminder: original.lastReminder,
            message: original.message,
          },
        };
      };

      return {
        sent: sent.map(map).reverse(),
        received: received.map(map).reverse(),
      };
    },

    getPetSpecies: async (playerId: string, api) => {
      const data = (
        await api.searchPlayers({
          where: {
            id: {
              isOneOf: [playerId],
            },
          },
          limit: 1,
        })
      ).results[0];

      return { species: data.petSpecies, name: data.petName, age: data.petAge };
    },
  });
};

export type DuoFriend = {
  id: string;
  profile: {
    name: string;
    photo: string;
    visits: number;
    visited: number;
    lastLogin: number;
  };
  pet: {
    name: string;
    species: PetSpecies;
    age: number;
  };
  invitation: {
    item: DuoItem;
    timestamp: number;
    status: 'pending' | 'accepted' | 'cancelled' | 'declined';
    lastReminder: number;
    message?: string;
  };
};
